import * as React from "react";
import { Input, message, Tooltip, Select, Space } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { SearchQueriesDropdown } from "Pages/Project/Documents/Components/Search/SearchQueriesDropdown";
import { I18N_NS } from "Config/i18n";
import { WithTranslation, withTranslation } from "react-i18next";
import { debounce } from "lodash";

interface SearchBarProps extends WithTranslation {
  ref?: React.Ref<React.Component>;
  autoFocus?: boolean;
  onSearch: (value: string) => Promise<void> | void;
  onShowSearchModal?: () => void;
  onChange?: (x: string) => void;
  queries?: boolean;
  searchIconTooltip?: string;
  settingsIconTooltip?: string;
  searchOnChange?: boolean;
  placeholder: string;
  /* Allow to render "clear" button with not empty input */
  hideClear?: boolean;
  style?: React.CSSProperties;
  defaultValue?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  select?: { [key: string]: any };
  onPressEnter?: (e?: React.KeyboardEvent) => void;
  forbidEmptySearchString?: boolean;
  inputDataTestId?: string;
}

interface SearchBarState {
  value: string;
  loading: boolean;
}

export const SearchBar = withTranslation(I18N_NS.PROJECT, { withRef: true })(
  class extends React.Component<SearchBarProps, SearchBarState> {
    constructor(props: SearchBarProps) {
      super(props);
      this.state = {
        value: props.defaultValue || "",
        loading: false,
      };
    }

    componentDidUpdate(prevProps: SearchBarProps) {
      if (prevProps.defaultValue !== this.props.defaultValue) {
        this.setState({ value: this.props.defaultValue });
      }
    }

    handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { searchOnChange, onSearch, onChange } = this.props;
      const { value } = e.target;

      this.setState({ value }, () => {
        if (searchOnChange) {
          onSearch(value);
        }
        onChange && onChange(value);
      });
    };

    handleClearSearch = () => {
      const debouncedSearch = debounce((value: string) => {
        this.props.onSearch(value);
      }, 300);
      this.setState({ value: "" }, async () => {
        this.props.onChange && this.props.onChange("");
        debouncedSearch("");
      });
    };

    handleSearch = async (e) => {
      e?.preventDefault();
      if (this.state.value.length) {
        try {
          this.setState({ loading: true });
          await this.props.onSearch(this.state.value);
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
          if (typeof this.props.onPressEnter === "function") this.props.onPressEnter();
        }
      } else {
        if (this.props.forbidEmptySearchString) {
          message.error(this.props.t("Please enter text for search"));
        } else {
          this.handleClearSearch();
        }
      }
    };

    defineClassName = () => {
      const { onShowSearchModal, searchOnChange } = this.props;

      return classNames("searchbar-options", {
        "bdrd-right": !searchOnChange && !onShowSearchModal,
        glass: !searchOnChange,
      });
    };

    suffixComponents(value: "clear" | "search" | "modal") {
      const { searchIconTooltip, settingsIconTooltip } = this.props;
      const components = {
        clear: (
          <div className="searchbar-options reset" onClick={this.handleClearSearch}>
            <FontAwesomeIcon icon={["fas", "times"]} size="xs" fixedWidth={true} />
          </div>
        ),
        search: (
          <div className={this.defineClassName()} onClick={this.handleSearch}>
            {this.state.loading ? (
              <FontAwesomeIcon
                icon={["fas", "circle-notch"]}
                size="1x"
                fixedWidth={true}
                spin={true}
                color={"#8992a1"}
              />
            ) : (
              <Tooltip title={searchIconTooltip}>
                <FontAwesomeIcon icon={["fas", "search"]} size="1x" fixedWidth={true} />
              </Tooltip>
            )}
          </div>
        ),
        modal: (
          <Tooltip title={settingsIconTooltip}>
            <div className="searchbar-options additional" onClick={this.props.onShowSearchModal}>
              <FontAwesomeIcon icon={["fas", "sliders-h"]} size="1x" fixedWidth={true} />
            </div>
          </Tooltip>
        ),
      };
      return components[value];
    }

    renderSuffix = () => {
      const { onShowSearchModal, searchOnChange, hideClear, queries, searchIconTooltip, settingsIconTooltip } =
        this.props;
      const { value } = this.state;

      return (
        <div className="searchbar-suffix">
          {!hideClear && value.length ? this.suffixComponents("clear") : ""}
          {!searchOnChange && (
            <div className={this.defineClassName()} onClick={this.handleSearch}>
              {this.state.loading ? (
                <FontAwesomeIcon
                  icon={["fas", "circle-notch"]}
                  size="1x"
                  fixedWidth={true}
                  spin={true}
                  color={"#8992a1"}
                />
              ) : (
                <Tooltip title={searchIconTooltip}>
                  <FontAwesomeIcon icon={["fas", "search"]} size="1x" fixedWidth={true} />
                </Tooltip>
              )}
            </div>
          )}
          {queries && <SearchQueriesDropdown />}
          {onShowSearchModal && (
            <Tooltip title={settingsIconTooltip}>
              <div className="searchbar-options additional" onClick={this.props.onShowSearchModal}>
                <FontAwesomeIcon icon={["fas", "sliders-h"]} size="1x" fixedWidth={true} />
              </div>
            </Tooltip>
          )}
        </div>
      );
    };

    renderPrefix = () => {
      return (
        <div className={this.defineClassName()} style={{ padding: "0 10px" }} onClick={this.handleSearch}>
          <FontAwesomeIcon icon={["fas", "search"]} size="1x" fixedWidth={true} />
        </div>
      );
    };

    render() {
      const { placeholder, style, searchOnChange, select, autoFocus, inputDataTestId } = this.props;
      return (
        <div className="searchbar-wrap" style={style}>
          <Space.Compact block>
            <Input
              autoFocus={autoFocus}
              data-testid={inputDataTestId}
              style={select && { flex: 1 }}
              placeholder={placeholder}
              value={this.state.value}
              prefix={searchOnChange && this.renderPrefix()}
              suffix={!select && this.renderSuffix()}
              onChange={this.handleInputChange}
              onPressEnter={this.handleSearch}
            />
            {select && (
              <>
                <Select defaultValue={select.defaultValue} style={{ width: 220 }} onChange={select.onChange}>
                  {select.options.map((item) => {
                    return (
                      <Select.Option key={item.value} value={item.value} disabled={item.disabled}>
                        {item.title}
                      </Select.Option>
                    );
                  })}
                </Select>
                <Input style={{ width: 150 }} value="" suffix={this.renderSuffix()} />
              </>
            )}
          </Space.Compact>
        </div>
      );
    }
  }
);
